import React, { useMemo, useRef, useState } from 'react';
import { GripVerticalIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { DiscountType, InvoiceLine, Product } from '../../../types/pro';
import { krPrecise } from '../../../utils/pro';
import { lineAmounts } from '../../../utils/invoice';
interface InvoiceLinesProps {
  lines: InvoiceLine[];
  products: Product[];
  pricesIncludeVat: boolean;
  readOnly: boolean;
  onChange: (lines: InvoiceLine[]) => void;
  onAddLine: () => void;
}
const VAT_OPTIONS = [{
  value: 0.25,
  label: '25 %'
}, {
  value: 0,
  label: '0 %'
}];
function normalize(value: string): string {
  return value.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa');
}

/**
 * The invoice table, edited in place. Typing in the description searches the
 * product list, but nothing forces you through it — a one-off line is just
 * text, a quantity and a price.
 */
export function InvoiceLines({
  lines,
  products,
  pricesIncludeVat,
  readOnly,
  onChange,
  onAddLine
}: InvoiceLinesProps) {
  const [openSuggestFor, setOpenSuggestFor] = useState<string | null>(null);
  const blurTimer = useRef<number | null>(null);
  const setLine = (id: string, changes: Partial<InvoiceLine>) => onChange(lines.map(line => line.id === id ? {
    ...line,
    ...changes
  } : line));
  const removeLine = (id: string) => onChange(lines.filter(line => line.id !== id));
  const suggestionsFor = (line: InvoiceLine): Product[] => {
    const term = normalize(line.description.trim());
    if (term.length < 1) return products.slice(0, 6);
    return products.filter(product => normalize(`${product.productNo} ${product.name}`).includes(term)).slice(0, 6);
  };
  const applyProduct = (lineId: string, product: Product) => {
    setLine(lineId, {
      description: product.name,
      unit: product.unit,
      unitPrice: product.price,
      vatRate: product.vatRate,
      productId: product.id
    });
    setOpenSuggestFor(null);
  };
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500 disabled:hover:border-transparent';
  const head = 'px-2 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500';
  const totalLabel = pricesIncludeVat ? 'Beløb inkl.' : 'Beløb';
  return <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-357" data-mp="1fdoso3fn28y0">
      <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-3.5" data-id="element-358" data-mp="1aevhihntqu10">
        <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-359" data-mp="1p3rmg5ul3qxe">
          Fakturalinjer
        </h2>
        <span className="text-xs text-stone-400" data-id="element-360" data-mp="1k714te34sn9v">
          {lines.length} {lines.length === 1 ? 'linje' : 'linjer'}
        </span>
      </header>

      <div className="overflow-x-auto" data-id="element-361" data-mp="1u3w4i0b54tpq">
        <table className="w-full min-w-[820px] text-sm" data-id="element-362" data-mp="1u4xe00cid85c">
          <thead data-id="element-363" data-mp="2cs6lk0svg9cn">
            <tr className="border-b border-stone-100 bg-stone-50 text-left" data-id="element-364" data-mp="2crohwj3vqsup">
              <th scope="col" className="w-8" data-id="element-365" data-mp="2hrat6n05dz0v" />
              <th scope="col" className={head} data-id="element-366" data-mp="2hpzqyrmcozbv">
                Produkt / beskrivelse
              </th>
              <th scope="col" className={`${head} text-right`} data-id="element-367" data-mp="23qqarry5a57w">
                Antal
              </th>
              <th scope="col" className={head} data-id="element-368" data-mp="28p1j1y8sk7o0">
                Enhed
              </th>
              <th scope="col" className={`${head} text-right`} data-id="element-369" data-mp="1u0wzks1wzs46">
                Stk. pris
              </th>
              <th scope="col" className={`${head} text-right`} data-id="element-370" data-mp="1ytzvv7y2tfo2">
                Rabat
              </th>
              <th scope="col" className={head} data-id="element-371" data-mp="1l2l9cr4ymwc9">
                Moms
              </th>
              <th scope="col" className={`${head} text-right`} data-id="element-372" data-mp="1p3ewvjehq94m">
                {totalLabel}
              </th>
              <th scope="col" className="w-10" data-id="element-373" data-mp="1b3mcs6xgyr5m" />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100" data-id="element-374" data-mp="1g0m8pp0ludj0">
            {lines.map(line => {
            const amounts = lineAmounts(line, pricesIncludeVat);
            const suggestions = openSuggestFor === line.id ? suggestionsFor(line) : [];
            return <tr key={line.id} className="align-top" data-id="element-375" data-mp="29m02alufyrlh">
                  <td className="px-1 pt-3 text-stone-300" data-id="element-376" data-mp="24nyght5mse6b">
                    <GripVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-377" data-mp="24nf936dlx86j" />
                  </td>

                  <td className="relative px-1 py-1" data-id="element-378" data-mp="1zq5d17vppy9q">
                    <input value={line.description} disabled={readOnly} onChange={e => {
                  setLine(line.id, {
                    description: e.target.value,
                    productId: undefined
                  });
                  setOpenSuggestFor(line.id);
                }} onFocus={() => setOpenSuggestFor(line.id)} onBlur={() => {
                  // Let a click on a suggestion land before closing.
                  blurTimer.current = window.setTimeout(() => setOpenSuggestFor(null), 140);
                }} onKeyDown={e => {
                  if (e.key === 'Escape') setOpenSuggestFor(null);
                  if (e.key === 'Enter' && suggestions[0]) {
                    e.preventDefault();
                    applyProduct(line.id, suggestions[0]);
                  }
                }} placeholder="Skriv produkt eller beskrivelse…" aria-label="Beskrivelse" className={cell} data-id="element-379" data-mp="1up99ivf9we4u" />

                    {suggestions.length > 0 && !readOnly && <ul role="listbox" aria-label="Foreslåede produkter" className="absolute left-1 right-1 top-full z-20 mt-1 overflow-hidden rounded-lg border border-stone-200 bg-white shadow-lg" data-id="element-380" data-mp="ty20mxqv42sw">
                        {suggestions.map(product => <li key={product.id} data-id="element-381" data-mp="33xbz019uvr9d">
                            <button type="button" onMouseDown={e => {
                      e.preventDefault();
                      if (blurTimer.current) window.clearTimeout(blurTimer.current);
                      applyProduct(line.id, product);
                    }} className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-stone-50" data-id="element-382" data-mp="2yzist88jyx93">
                              <span className="font-mono text-xs text-stone-400" data-id="element-383" data-mp="1shnav6kv8m6z">
                                {product.productNo}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-sm text-stone-900" data-id="element-384" data-mp="319vixd0vuzb">
                                {product.name}
                              </span>
                              <span className="whitespace-nowrap text-xs tabular-nums text-stone-500" data-id="element-385" data-mp="3q7kbiq0fhmam">
                                {product.price.toLocaleString('da-DK')} kr /{' '}
                                {product.unit}
                              </span>
                            </button>
                          </li>)}
                      </ul>}
                  </td>

                  <td className="w-20 px-1 py-1" data-id="element-386" data-mp="crcmyt4hx0vj">
                    <input type="number" step="0.01" value={line.quantity} disabled={readOnly} onChange={e => setLine(line.id, {
                  quantity: parseFloat(e.target.value) || 0
                })} aria-label="Antal" className={`${cell} text-right tabular-nums`} data-id="element-387" data-mp="hr8u318hp8fb" />
                  </td>

                  <td className="w-24 px-1 py-1" data-id="element-388" data-mp="3k7wkc0g9kxx3">
                    <input value={line.unit} disabled={readOnly} onChange={e => setLine(line.id, {
                  unit: e.target.value
                })} aria-label="Enhed" className={cell} data-id="element-389" data-mp="3f7ryrw0i1d00" />
                  </td>

                  <td className="w-28 px-1 py-1" data-id="element-390" data-mp="701p7t1aljmu">
                    <input type="number" step="0.01" value={line.unitPrice} disabled={readOnly} onChange={e => setLine(line.id, {
                  unitPrice: parseFloat(e.target.value) || 0
                })} aria-label="Stykpris" className={`${cell} text-right tabular-nums`} data-id="element-391" data-mp="6yiba933zko8" />
                  </td>

                  <td className="w-28 px-1 py-1" data-id="element-392" data-mp="1bzhtnpegmnz4">
                    <span className="flex items-center gap-1" data-id="element-393" data-mp="1bxnbujyhjbz9">
                      <input type="number" step="0.01" value={line.discount} disabled={readOnly} onChange={e => setLine(line.id, {
                    discount: parseFloat(e.target.value) || 0
                  })} aria-label="Rabat" className={`${cell} text-right tabular-nums`} data-id="element-394" data-mp="1gyl1fhltr8as" />
                      <select value={line.discountType} disabled={readOnly} onChange={e => setLine(line.id, {
                    discountType: e.target.value as DiscountType
                  })} aria-label="Rabattype" className="rounded border border-transparent bg-transparent py-1.5 text-xs text-stone-500 outline-none hover:border-stone-200 focus:ring-2 focus:ring-brand-500" data-id="element-395" data-mp="24pqfbr87fqig">
                        <option value="procent" data-id="element-396" data-mp="2ev4vviduajxt">%</option>
                        <option value="beloeb" data-id="element-397" data-mp="2jtpwvk0x17nb">kr</option>
                      </select>
                    </span>
                  </td>

                  <td className="w-20 px-1 py-1" data-id="element-398" data-mp="2nwmbkznc4jlz">
                    <select value={line.vatRate} disabled={readOnly} onChange={e => setLine(line.id, {
                  vatRate: parseFloat(e.target.value)
                })} aria-label="Momssats" className={cell} data-id="element-399" data-mp="2svpeadfuedqs">
                      {VAT_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-400" data-mp="m53y1uv8n585">
                          {option.label}
                        </option>)}
                    </select>
                  </td>

                  <td className="whitespace-nowrap px-2 py-3 text-right font-semibold tabular-nums text-stone-900" data-id="element-401" data-mp="19w9b629snjer">
                    {krPrecise(pricesIncludeVat ? amounts.total : amounts.net)}
                    {amounts.discountAmount > 0 && <span className="block text-xs font-normal text-emerald-600" data-id="element-402" data-mp="zwqxg3kh8tv5">
                        −{krPrecise(amounts.discountAmount)}
                      </span>}
                  </td>

                  <td className="px-1 py-2" data-id="element-403" data-mp="3kmg8e5i27vik">
                    {!readOnly && <button onClick={() => removeLine(line.id)} aria-label="Slet linje" className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-404" data-mp="3glnx76pmngjn">
                        <Trash2Icon className="h-4 w-4" data-id="element-405" data-mp="3855gbsg1hzik" />
                      </button>}
                  </td>
                </tr>;
          })}

            {lines.length === 0 && <tr data-id="element-406" data-mp="2zcbybn97km10">
                <td colSpan={9} className="px-5 py-10 text-center text-sm text-stone-400" data-id="element-407" data-mp="2zbshyaz7i5p3">
                  Ingen linjer endnu — tilføj en linje eller hent fra opgaven.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {!readOnly && <div className="border-t border-stone-100 p-3" data-id="element-408" data-mp="2gftwue4k57rk">
          <button onClick={onAddLine} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 transition-colors hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700" data-id="element-409" data-mp="2kgcskj9kmxz1">
            <PlusIcon className="h-4 w-4" data-id="element-410" data-mp="2asi8rahzpt8j" />
            Tilføj fakturalinje
          </button>
        </div>}
    </section>;
}